import { useEffect, useId, useState } from 'react';
import { formatCopy } from '@/i18n/ns';
import { useAppLocale } from '@/i18n/context';
import { readQueryLimit, saveQueryLimit, type ConnectionQueryLimit } from './api';
import { readAccessCopy } from './copy';

// Choices an admin picks for a use case: quick dashboards to long reports.
const CHOICES = [30, 60, 120, 300, 600, 900, 1800, 3600];
const duration = (seconds: number, copy: ReturnType<typeof readAccessCopy>) =>
  seconds % 60 === 0
    ? formatCopy(copy.minutes, { count: seconds / 60 })
    : formatCopy(copy.seconds, { count: seconds });

// The inherited limit is the first option, so it is not offered twice unless
// the company chose exactly that value.
function limitChoices(limit: ConnectionQueryLimit) {
  const inherited = limit.sourceSeconds ?? limit.defaultSeconds;
  const saved = limit.queryTimeoutSeconds;
  return [...new Set([...CHOICES, ...(saved === null ? [] : [saved])])]
    .filter(
      (seconds) => seconds <= limit.maxSeconds && (seconds !== inherited || seconds === saved),
    )
    .sort((a, b) => a - b);
}

/** A company admin's choice of how long one read may run on this database. */
export function QueryLimitControl({ connectionId }: { connectionId: string }) {
  const copy = readAccessCopy(useAppLocale());
  const id = useId();
  const [limit, setLimit] = useState<ConnectionQueryLimit | null>(null);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error' | 'loadError'>('idle');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    readQueryLimit(connectionId)
      .then((value) => {
        if (!active) return;
        setLimit(value);
        setState('idle');
      })
      .catch(() => active && setState('loadError'));
    return () => {
      active = false;
    };
  }, [connectionId, attempt]);
  const save = async (value: string) => {
    setState('saving');
    try {
      setLimit(await saveQueryLimit(connectionId, value === '' ? null : Number(value)));
      setState('saved');
    } catch {
      setState('error');
    }
  };
  const message = {
    idle: '',
    saving: copy.queryLimitSaving,
    saved: copy.queryLimitSaved,
    error: copy.queryLimitError,
    loadError: copy.queryLimitLoadError,
  }[state];
  const inherited = limit ? (limit.sourceSeconds ?? limit.defaultSeconds) : null;
  return (
    <div className="ws-db-query-limit">
      {limit && inherited !== null && (
        <>
          <label htmlFor={id}>{copy.queryLimitLabel}</label>
          <select
            id={id}
            value={limit.queryTimeoutSeconds === null ? '' : String(limit.queryTimeoutSeconds)}
            disabled={state === 'saving'}
            aria-describedby={`${id}-hint`}
            onChange={(event) => void save(event.target.value)}
          >
            <option value="">
              {formatCopy(
                limit.sourceSeconds === null ? copy.queryLimitDefault : copy.queryLimitSource,
                { value: duration(inherited, copy) },
              )}
            </option>
            {limitChoices(limit).map((seconds) => (
              <option key={seconds} value={String(seconds)}>
                {duration(seconds, copy)}
              </option>
            ))}
          </select>
          <p id={`${id}-hint`}>
            {formatCopy(copy.queryLimitHint, { default: duration(limit.defaultSeconds, copy) })}
          </p>
        </>
      )}
      {/* Always mounted, so screen readers announce each change. */}
      <p role="status" aria-live="polite">
        {message}
      </p>
      {state === 'loadError' && (
        <button
          type="button"
          className="ws-text-button"
          onClick={() => setAttempt((value) => value + 1)}
        >
          {copy.queryLimitRetry}
        </button>
      )}
    </div>
  );
}
